// Lightweight i18n dictionary for the group-quiz UI strings.
//
// Why this exists separately from the quiz-question TSVs:
//   - The quiz questions themselves are translated via the per-locale TSV
//     files in /public. That covers the question text but leaves the
//     surrounding UI strings English-only.
//   - We don't want to pull in `next-intl` or `react-i18next` for this
//     surface; the strings are bounded and stable. A plain dictionary
//     keeps the bundle slim and makes it obvious what's translated.
//
// Coverage today:
//   - Predict mini-game (lobby): full
//   - Predict-was-submitted recap (lobby): full
//   - Nudge stragglers (lobby): full
//   - Retake banner (/group/new): full
//   - "What you're joining" preview (/join): full
//   - Reveal: closing CTA + debate feedback + "more ways to share" +
//     "what people guessed" + retake shift overlay + baseline lines: full
//   - QuestionDossier popover labels: full
//   - QuestionDossier *content* (12 dossiers): English-only, separate
//     pass once authoring is locked in.
//   - "You are the X" callout content + per-axis insight sentences:
//     English-only, these are programmatically templated from many
//     branches; translating them is a deeper rewrite of insights.ts.

export type Locale = 'en' | 'de';

type Entry = { en: string; de: string };
type Dict = Record<string, Entry>;

const STRINGS: Dict = {
  // ----- Predict mini-game (lobby) -----
  'predict.pill': {
    en: 'While you wait',
    de: 'Während du wartest',
  },
  'predict.title': {
    en: 'Predict the group',
    de: 'Tipp die Gruppe',
  },
  'predict.intro.before': {
    en: "Before the reveal unlocks, take a guess. We'll show how close you got when the rest of the group finishes.",
    de: 'Bevor die Auswertung freigeschaltet wird, gib einen Tipp ab. Wir zeigen dir, wie nah du dran warst, sobald die Gruppe fertig ist.',
  },
  'predict.intro.after': {
    en: "The reveal is already unlocked, but you can still log a guess if you haven't peeked yet.",
    de: 'Die Auswertung ist schon freigeschaltet, aber du kannst noch tippen, wenn du sie noch nicht angesehen hast.',
  },
  'predict.honor': {
    en: "Honor system: only predict if you genuinely haven't seen the reveal yet.",
    de: 'Ehrensystem: tippe nur, wenn du die Auswertung wirklich noch nicht gesehen hast.',
  },
  'predict.cell.legend': {
    en: "Where will the group's average land on the cube?",
    de: 'Wo wird der Mittelwert der Gruppe auf dem Würfel landen?',
  },
  'predict.gov.legend': {
    en: 'On authority, where will the group lean?',
    de: 'Wie autoritär wird die Gruppe sich positionieren?',
  },
  'predict.optional': {
    en: '(optional)',
    de: '(optional)',
  },
  'predict.gov.libertarian': { en: 'Libertarian', de: 'Libertär' },
  'predict.gov.middle': { en: 'Middle', de: 'Mitte' },
  'predict.gov.authoritarian': { en: 'Authoritarian', de: 'Autoritär' },
  'predict.cell.left': { en: 'Left', de: 'Links' },
  'predict.cell.center': { en: 'Center', de: 'Mitte' },
  'predict.cell.right': { en: 'Right', de: 'Rechts' },
  'predict.cell.progressive': { en: 'Progressive', de: 'Progressiv' },
  'predict.cell.middle': { en: 'Middle', de: 'Mitte' },
  'predict.cell.conservative': { en: 'Conservative', de: 'Konservativ' },
  'predict.archetype.legend': {
    en: 'What archetype do you think the group will land on?',
    de: 'Welches Archetyp wird die Gruppe deiner Meinung nach werden?',
  },
  'predict.archetype.placeholder': {
    en: '…or type any of the 81 detailed archetypes',
    de: '…oder gib eines der 81 detaillierten Archetypen ein',
  },
  'predict.submit': {
    en: 'Lock in my prediction',
    de: 'Tipp abgeben',
  },
  'predict.submitting': {
    en: 'Saving…',
    de: 'Speichern…',
  },
  'predict.preview_only': {
    en: 'Preview only',
    de: 'Nur Vorschau',
  },
  'predict.preview_banner': {
    en: "Preview only: impersonation mode. Predictions can't be saved as another member.",
    de: 'Nur Vorschau: Impersonierungsmodus. Tipps können nicht für andere Mitglieder gespeichert werden.',
  },
  'predict.error': {
    en: 'Could not save your prediction. Try again in a moment.',
    de: 'Tipp konnte nicht gespeichert werden. Bitte versuche es gleich nochmal.',
  },
  'predict.recap.title': {
    en: 'Your prediction is in',
    de: 'Dein Tipp ist eingegangen',
  },
  'predict.recap.body': {
    en: 'You guessed {cell} on the cube, landing on {archetype}',
    de: 'Du hast {cell} auf dem Würfel getippt, mit Archetyp {archetype}',
  },
  'predict.recap.body.with_gov': {
    en: ', with the group leaning {gov}',
    de: ', mit Tendenz {gov}',
  },
  'predict.recap.body.tail': {
    en: ". We'll show how close you got when the reveal unlocks.",
    de: '. Wir zeigen dir, wie nah du dran warst, sobald die Auswertung freigeschaltet wird.',
  },
  'predict.recap.privacy': {
    en: 'Predictions stay private until at least 2 members have finished.',
    de: 'Tipps bleiben privat, bis mindestens 2 Mitglieder fertig sind.',
  },
  'predict.edit': {
    en: 'Edit prediction',
    de: 'Tipp bearbeiten',
  },

  // ----- Nudge stragglers -----
  'nudge.button.idle': {
    en: 'Nudge the stragglers',
    de: 'Nachzügler anstupsen',
  },
  'nudge.button.copied': {
    en: 'Copied: paste it to them',
    de: 'Kopiert: füg es ihnen ein',
  },
  'nudge.helper': {
    en: 'Copies a pre-written reminder with the invite link, ready to paste into a text or chat.',
    de: 'Kopiert eine fertige Erinnerung mit dem Einladungslink, bereit zum Einfügen.',
  },
  'nudge.text.last_one': {
    en: "Hey, you're the last one we're waiting on for {group} on Votely. Takes ~3 minutes: {url}",
    de: 'Hey, du bist die letzte Person, auf die wir bei {group} auf Votely warten. Dauert ca. 3 Minuten: {url}',
  },
  'nudge.text.few': {
    en: "Hey, still waiting on a few people for {group} on Votely. Takes ~3 minutes: {url}",
    de: 'Hey, wir warten noch auf ein paar Leute für {group} auf Votely. Dauert ca. 3 Minuten: {url}',
  },

  // ----- Retake banner (/group/new) -----
  'retake.title': { en: 'Retake mode', de: 'Wiederholungsmodus' },
  'retake.body.prefix': {
    en: "You're starting a follow-up to",
    de: 'Du startest eine Folgegruppe von',
  },
  'retake.body.unknown': {
    en: 'an earlier group',
    de: 'einer früheren Gruppe',
  },
  'retake.body.suffix': {
    en: 'Same 12 questions, fresh answers. After creating the group, share the new invite link with the same people who took the original.',
    de: 'Gleiche 12 Fragen, neue Antworten. Teile nach dem Erstellen den neuen Einladungslink mit denselben Leuten wie beim Original.',
  },
  'retake.body.suffix_approx': {
    en: 'Same 12 questions (or as close as we could get), fresh answers. After creating the group, share the new invite link with the same people who took the original.',
    de: 'Gleiche 12 Fragen (oder so nah dran wie möglich), neue Antworten. Teile nach dem Erstellen den neuen Einladungslink mit denselben Leuten wie beim Original.',
  },

  // ----- "What you're joining" preview (/join) -----
  'join.preview.title': {
    en: "What you're joining",
    de: 'Worauf du dich einlässt',
  },
  'join.preview.bullet1': {
    en: '12 questions, about 3 minutes. Same set for everyone in the group.',
    de: '12 Fragen, ca. 3 Minuten. Gleicher Satz für alle in der Gruppe.',
  },
  'join.preview.bullet2': {
    en: 'Your answers stay private until at least 2 members finish. Not even the host sees your specific answers before then.',
    de: 'Deine Antworten bleiben privat, bis mindestens 2 Mitglieder fertig sind. Auch der Host sieht sie vorher nicht.',
  },
  'join.preview.bullet3': {
    en: 'The reveal shows you where the group agrees, where the conversations are worth having, and your place in the group, with a 3D cube, per-question breakdown, and a printable handout.',
    de: 'Die Auswertung zeigt dir, wo die Gruppe einig ist, welche Gespräche lohnen, und deinen Platz darin, mit 3D-Würfel, Fragen-Aufschlüsselung und druckbarem Handout.',
  },
  'join.preview.bullet4': {
    en: 'Anonymous by default. No account required.',
    de: 'Standardmäßig anonym. Kein Konto erforderlich.',
  },
  'join.preview.spots_taken': {
    en: 'Currently {filled} of {cap} spots taken.',
    de: 'Aktuell {filled} von {cap} Plätzen vergeben.',
  },
  'join.preview.have_finished': {
    en: '{count} {pluralized} already finished.',
    de: '{count} {pluralized} schon fertig.',
  },

  // ----- Reveal: what people guessed -----
  'guessed.title': {
    en: 'What people guessed',
    de: 'Was Mitglieder getippt haben',
  },
  'guessed.intro': {
    en: "Before this reveal unlocked, members guessed where the group would land. Here's how they did.",
    de: 'Bevor diese Auswertung freigeschaltet wurde, haben Mitglieder geraten, wo die Gruppe landen würde. So sind sie gefahren.',
  },
  'guessed.count': {
    en: '{predicted} of {total} predicted.',
    de: '{predicted} von {total} haben getippt.',
  },
  'guessed.actual.cell': { en: 'Actual cell:', de: 'Tatsächliche Zelle:' },
  'guessed.actual.gov': { en: 'Actual governance lean:', de: 'Tatsächliche Autoritäts-Tendenz:' },
  'guessed.actual.archetype': { en: 'Actual archetype:', de: 'Tatsächliches Archetyp:' },
  'guessed.row.cell': { en: 'Cell:', de: 'Zelle:' },
  'guessed.row.gov': { en: 'Governance:', de: 'Autorität:' },
  'guessed.row.archetype': { en: 'Archetype:', de: 'Archetyp:' },
  'guessed.chip.nailed': { en: 'Nailed it', de: 'Voll getroffen' },
  'guessed.chip.close': { en: 'One cell off', de: 'Eine Zelle daneben' },
  'guessed.chip.off': { en: 'Off the mark', de: 'Daneben' },
  'guessed.chip.match': { en: 'Match', de: 'Treffer' },
  'guessed.chip.no_match': { en: 'No match', de: 'Kein Treffer' },
  'leaderboard.label': { en: 'Closest prediction', de: 'Bester Tipp' },
  'leaderboard.winner': {
    en: '{name} called it best, with {score} of {max} points.',
    de: '{name} hat am besten getippt, mit {score} von {max} Punkten.',
  },
  'leaderboard.tied': {
    en: 'Tied at {score} points: {names}.',
    de: 'Gleichstand bei {score} Punkten: {names}.',
  },

  // ----- Closing CTA + debate feedback -----
  'closing.title': {
    en: 'Bring this to a conversation',
    de: 'Mach ein Gespräch daraus',
  },
  'closing.body': {
    en: "Don't let the reveal be the end. Get a calendar invite for a debate night, pre-loaded with the {n} questions most worth slowing down on.",
    de: 'Lass die Auswertung nicht das Ende sein. Hol dir eine Kalendereinladung für einen Debattenabend, mit den {n} Fragen, bei denen es am meisten lohnt zu verweilen.',
  },
  'closing.cta': {
    en: 'Schedule a debate night',
    de: 'Debattenabend planen',
  },
  'closing.footer': {
    en: 'Defaults to next Saturday at 7pm local time. Edit it in your calendar app.',
    de: 'Standardmäßig nächsten Samstag um 19:00 Uhr Ortszeit. Bearbeite es in deinem Kalender.',
  },
  'feedback.label': {
    en: 'Already did this with your group? Tell us how it went.',
    de: 'Schon mit deiner Gruppe besprochen? Erzähl uns, wie es war.',
  },
  'feedback.placeholder': {
    en: 'What did you end up talking about, and what surprised you?',
    de: 'Worüber habt ihr am Ende geredet, und was hat euch überrascht?',
  },
  'feedback.helper': {
    en: 'Anonymous. Helps us tune future question sets.',
    de: 'Anonym. Hilft uns, künftige Fragesätze zu verbessern.',
  },
  'feedback.send.idle': { en: 'Send', de: 'Senden' },
  'feedback.send.busy': { en: 'Sending…', de: 'Wird gesendet…' },
  'feedback.sent': { en: 'Thanks. Filed away.', de: 'Danke. Notiert.' },
  'feedback.error': {
    en: 'Could not save. Try again in a moment.',
    de: 'Konnte nicht gespeichert werden. Bitte versuche es gleich nochmal.',
  },

  // ----- More ways to share -----
  'share.title': { en: 'More ways to share this', de: 'Mehr Wege zum Teilen' },
  'share.intro': {
    en: "Same data, different formats. Pick the one that fits where you're sharing.",
    de: 'Gleiche Daten, andere Formate. Wähl das passende für deine Plattform.',
  },
  'share.report.title': {
    en: 'Full report (PDF-ready)',
    de: 'Vollständiger Bericht (PDF-fertig)',
  },
  'share.report.body': {
    en: '12 pages: cover, per-member sub-pages, alignment matrix, conversation guide. Print to PDF from your browser.',
    de: '12 Seiten: Cover, Mitgliederseiten, Alignment-Matrix, Gesprächsleitfaden. Drucken Sie als PDF aus dem Browser.',
  },
  'share.print.title': { en: 'One-page handout', de: 'Einseitiges Handout' },
  'share.print.body': {
    en: 'Black-and-white, single sheet. Good for a dinner table or a classroom.',
    de: 'Schwarz-weiß, eine Seite. Ideal für den Esstisch oder das Klassenzimmer.',
  },
  'share.embed.title': { en: 'Embeddable card', de: 'Einbettbare Karte' },
  'share.embed.body': {
    en: 'Iframe-safe 800x500 card for Substack, Notion, or a blog post. Auto-updates as members finish.',
    de: 'Iframe-sichere 800x500 Karte für Substack, Notion oder Blogposts. Aktualisiert sich, sobald Mitglieder fertig sind.',
  },

  // ----- Retake shift overlay -----
  'retake_shift.pill': {
    en: 'Compared to last time',
    de: 'Im Vergleich zum letzten Mal',
  },
  'retake_shift.subtitle.days': {
    en: '{days} days ago',
    de: 'vor {days} Tagen',
  },
  'retake_shift.intro': {
    en: "Here's how the group's centroid moved on each axis.",
    de: 'So hat sich der Mittelwert der Gruppe auf jeder Achse verschoben.',
  },
  'retake_shift.axis.economic': { en: 'Economic', de: 'Ökonomisch' },
  'retake_shift.axis.authority': { en: 'Governance', de: 'Autorität' },
  'retake_shift.axis.social': { en: 'Social', de: 'Sozial' },
  'retake_shift.unchanged': { en: 'Unchanged', de: 'Unverändert' },
  'retake_shift.shift.template': {
    en: '{magnitude} pts {direction}',
    de: '{magnitude} Pkt. {direction}',
  },
  'retake_shift.dir.right': { en: 'right', de: 'rechts' },
  'retake_shift.dir.left': { en: 'left', de: 'links' },
  'retake_shift.dir.more_auth': { en: 'more authoritarian', de: 'autoritärer' },
  'retake_shift.dir.more_lib': { en: 'more libertarian', de: 'libertärer' },
  'retake_shift.dir.more_cons': { en: 'more conservative', de: 'konservativer' },
  'retake_shift.dir.more_prog': { en: 'more progressive', de: 'progressiver' },
  'retake_shift.who_moved': { en: 'Who moved', de: 'Wer sich bewegt hat' },
  'retake_shift.who_moved.intro': {
    en: 'Matched by display name (biggest movers first).',
    de: 'Per Anzeigename zugeordnet (größte Bewegung zuerst).',
  },
  'retake_shift.unmatched': {
    en: "{n} {pluralized} couldn't be matched (different name or shared with someone else).",
    de: "{n} {pluralized} konnte nicht zugeordnet werden (anderer Name oder doppelt vergeben).",
  },
  'retake_shift.row.econ': { en: 'Econ', de: 'Ökon.' },
  'retake_shift.row.auth': { en: 'Governance', de: 'Autorität' },
  'retake_shift.row.social': { en: 'Social', de: 'Sozial' },
  'retake_shift.value.flat': { en: 'flat', de: 'gleich' },

  // ----- Baseline (per-axis card) -----
  'baseline.small_delta': {
    en: 'Sits within a few points of the typical Votely group on this axis.',
    de: 'Liegt nah am Durchschnitt der Votely-Gruppen auf dieser Achse.',
  },
  'baseline.template': {
    en: 'Your group sits about {magnitude} points {direction} than the average Votely taker.',
    de: 'Eure Gruppe liegt etwa {magnitude} Punkte {direction} als der Votely-Durchschnitt.',
  },
  'baseline.dir.further_left': { en: 'further left', de: 'weiter links' },
  'baseline.dir.further_right': { en: 'further right', de: 'weiter rechts' },
  'baseline.dir.further_prog': { en: 'further progressive', de: 'progressiver' },
  'baseline.dir.further_cons': { en: 'further conservative', de: 'konservativer' },

  // ----- QuestionDossier popover -----
  'dossier.toggle.open': {
    en: 'Why this question matters',
    de: 'Warum diese Frage zählt',
  },
  'dossier.toggle.close': { en: 'Hide context', de: 'Kontext ausblenden' },
  'dossier.disclaimer': {
    en: 'Our authored take, not peer-reviewed. Both sides are written to be defensible by a thoughtful person who holds them.',
    de: 'Unsere eigene Darstellung, nicht peer-reviewed. Beide Seiten sind so geschrieben, dass eine durchdachte Person sie vertreten könnte.',
  },
  'dossier.framing': { en: "What it's really asking:", de: 'Worum es wirklich geht:' },
  'dossier.case.agree': { en: 'The case to agree', de: 'Argumente dafür' },
  'dossier.case.disagree': { en: 'The case to disagree', de: 'Argumente dagegen' },
  'dossier.sources.label': { en: 'Further reading', de: 'Weiterführende Quellen' },
  'dossier.correction.prefix': {
    en: 'Spot something wrong here?',
    de: 'Etwas falsch hier?',
  },
  'dossier.correction.link': { en: 'Tell us', de: 'Sag es uns' },
};

// Substitute {var} tokens in a template string with stringified values.
// Missing vars are left in place so we can spot them during QA.
function interpolate(template: string, vars: Record<string, string | number>): string {
  let out = template;
  for (const [k, v] of Object.entries(vars)) {
    out = out.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
  }
  return out;
}

// Resolve a string by key + locale. Falls back to en, then to the key
// itself (so missing translations surface visibly during QA).
export function t(
  key: string,
  locale: Locale,
  vars: Record<string, string | number> = {},
): string {
  const entry = STRINGS[key];
  const raw = entry?.[locale] ?? entry?.en ?? key;
  return Object.keys(vars).length ? interpolate(raw, vars) : raw;
}
